import type { AyanamsaId } from '@astrally/shared';

/**
 * Sidereal ayanāṁśa models.
 *
 * Each is anchored at its widely-cited J2000 value and carried forward by the
 * IAU accumulated general precession in ecliptic longitude (mean equinox;
 * nutation is not applied). They differ only by a constant offset of a few
 * arcminutes, which is what distinguishes the schools in practice.
 *
 * Accuracy vs Swiss Ephemeris: within roughly ±30 arcseconds over 1900–2050
 * (≈ 4 seconds of birth time for the Moon) — see docs/ASTROLOGY_ENGINE.md.
 *
 * Which to offer matters regionally: Lahiri (Chitrapakṣa) is the Indian
 * government standard and the pan-India default, Raman has a following in
 * Karnataka and among B.V. Raman's school, and KP (Krishnamurti Paddhati) is
 * very widely used in Tamil Nadu and the wider South.
 */

const PRECESSION_DEG_PER_CENTURY = 1.3969713;
const PRECESSION_QUADRATIC = 0.0003086;

/** J2000 ayanamsa values in degrees. */
const J2000_VALUES: Record<AyanamsaId, number> = {
  // 23°51'11" — Indian Calendar Reform Committee / government standard.
  lahiri: 23.85306,
  // ≈ 22°28'32" — B. V. Raman's school; ~21' behind Lahiri.
  raman: 22.47556,
  // ≈ 23°45'51" — K. S. Krishnamurti; ~5' behind Lahiri.
  kp: 23.76417,
};

function ayanamsaFor(id: AyanamsaId, julianCenturiesTT: number): number {
  const T = julianCenturiesTT;
  return J2000_VALUES[id] + PRECESSION_DEG_PER_CENTURY * T + PRECESSION_QUADRATIC * T * T;
}

export function lahiriAyanamsa(julianCenturiesTT: number): number {
  return ayanamsaFor('lahiri', julianCenturiesTT);
}

export interface AyanamsaProvider {
  id: AyanamsaId;
  /** Display name for the settings UI. */
  label: string;
  /** Short note on who uses it. */
  note: string;
  valueDeg(julianCenturiesTT: number): number;
}

export const AYANAMSAS: Record<AyanamsaId, AyanamsaProvider> = {
  lahiri: {
    id: 'lahiri',
    label: 'Lahiri (Chitrapaksha)',
    note: 'Indian government standard — the usual default across India.',
    valueDeg: (T) => ayanamsaFor('lahiri', T),
  },
  raman: {
    id: 'raman',
    label: 'Raman',
    note: "B. V. Raman's school; about 21 arcminutes behind Lahiri.",
    valueDeg: (T) => ayanamsaFor('raman', T),
  },
  kp: {
    id: 'kp',
    label: 'KP (Krishnamurti)',
    note: 'Krishnamurti Paddhati; widely used in Tamil Nadu and the South.',
    valueDeg: (T) => ayanamsaFor('kp', T),
  },
};

export function getAyanamsa(id: AyanamsaId): AyanamsaProvider {
  const p = AYANAMSAS[id];
  if (!p) throw new Error(`Unknown ayanamsa: ${id}`);
  return p;
}

/** Ordered list for settings UIs. */
export const AYANAMSA_OPTIONS: AyanamsaProvider[] = [
  AYANAMSAS.lahiri,
  AYANAMSAS.raman,
  AYANAMSAS.kp,
];
